/**
 * Financial Table API Service
 * 
 * Provides standardized methods for fetching financial data for HierarchicalFinancialTable
 * Supports current and snapshot data with dynamic month calculation
 * 
 * @author Financial Table Team
 * @version 1.0.0
 */

import api from './api';

// Check if user is authenticated
const isAuthenticated = () => {
    const token = localStorage.getItem('accessToken');
    return !!token;
};

// Get auth headers
const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

class FinancialTableService {
    constructor() {
        this.cache = new Map();
        this.defaultTTL = 5 * 60 * 1000; // 5 minutes
        this.maxCacheSize = 20;

        this.endpoints = {
            // Primary endpoint: campaigns data from router
            financial: 'campaigns-data/',
            // Fallback endpoints to try (remove leading slash to avoid duplicate)
            alternatives: [
                'campaigns-data/',
                'total-data/',
                'all-campaigns/',
                'reports/brand-summaries/',
                'dashboard/budget-data/'
            ]
        };

        this.defaultConfig = {
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            }
        };
    }

    /**
     * Generate cache key for request options
     */
    getCacheKey(options) {
        const { 
            year, 
            month, 
            brandId, 
            campaignId, 
            mediaType, 
            isSnapshotMode = false, 
            snapshotDate = null 
        } = options;
        
        const baseKey = `financial_${year || 'all'}_${month || 'all'}_${brandId || 'all'}_${campaignId || 'all'}_${mediaType || 'all'}`;
        
        if (isSnapshotMode && snapshotDate) {
            return `${baseKey}_snapshot_${snapshotDate}`;
        }
        
        return baseKey;
    }

    /**
     * Check if cached data is still valid
     */
    shouldUseCache(key) {
        const cached = this.cache.get(key);
        if (!cached) return false;
        
        const age = Date.now() - cached.timestamp;
        return age < this.defaultTTL;
    }

    /**
     * Store data in cache with TTL management
     */
    setCache(key, data) {
        if (this.cache.size >= this.maxCacheSize) {
            const oldestKey = this.cache.keys().next().value;
            this.cache.delete(oldestKey);
        }
        
        this.cache.set(key, {
            data,
            timestamp: Date.now(),
            key
        });
    }

    /**
     * Get cached data if available and valid
     */
    getCache(key) {
        if (this.shouldUseCache(key)) {
            return this.cache.get(key).data;
        }
        
        this.cache.delete(key);
        return null;
    }

    /**
     * Execute API call with retry mechanism
     */
    async apiCallWithRetry(apiCall, maxRetries = 3, retryDelay = 1000) {
        let lastError;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const result = await apiCall();
                return result;
            } catch (error) {
                lastError = error;
                
                if (error.response?.status >= 400 && error.response?.status < 500) {
                    throw error;
                }
                
                if (attempt < maxRetries) {
                    await new Promise(resolve => 
                        setTimeout(resolve, retryDelay * Math.pow(2, attempt - 1))
                    );
                }
            }
        }
        
        throw lastError;
    }

    /**
     * Calculate current month headers based on snapshot rules
     * If before 15th: use previous month's data
     * If after 15th: current month becomes "this month"
     */
    getCurrentMonthParams() {
        const today = new Date();
        const currentMonth = today.getMonth() + 1; // 1-12
        const currentYear = today.getFullYear();
        const currentDay = today.getDate();
        
        let thisMonth, lastMonth, thisYear, lastYear;
        
        if (currentDay < 15) {
            // Before 15th: still working with previous month's data
            thisMonth = currentMonth === 1 ? 12 : currentMonth - 1;
            thisYear = currentMonth === 1 ? currentYear - 1 : currentYear;
            lastMonth = thisMonth === 1 ? 12 : thisMonth - 1;
            lastYear = thisMonth === 1 ? thisYear - 1 : thisYear;
        } else {
            // After 15th: snapshot is ready, move to current month
            thisMonth = currentMonth;
            thisYear = currentYear;
            lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
            lastYear = currentMonth === 1 ? currentYear - 1 : currentYear;
        }
        
        return {
            thisMonth,
            thisYear,
            lastMonth,
            lastYear,
            isSnapshotReady: currentDay >= 15
        };
    }

    /**
     * Build API endpoint URL with parameters
     */
    buildEndpoint(params = {}, endpointPath = null) {
        const endpoint = endpointPath || this.endpoints.financial;
        const queryParams = new URLSearchParams();
        
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value);
            }
        });
        
        const queryString = queryParams.toString();
        return queryString ? `${endpoint}?${queryString}` : endpoint;
    }

    /**
     * Check if media type belongs to ICON (social) group
     * ICON = social media types
     * RS = Red Star = all other media types (paid media)
     */
    isIconMediaType(mediaType) {
        if (!mediaType) return false;
        
        const mediaTypeLower = mediaType.toLowerCase().trim();
        
        // Direct social media type
        if (mediaTypeLower === 'social') return true;
        
        // Check for social indicators in digital vendor or media type
        const socialKeywords = ['social', 'facebook', 'instagram', 'youtube', 'tiktok', 'twitter', 'linkedin'];
        return socialKeywords.some(keyword => mediaTypeLower.includes(keyword));
    }
    
    /**
     * Check if digital vendor indicates social media
     */
    isIconDigitalVendor(digitalVendor) {
        if (!digitalVendor) return false;
        
        const vendorLower = digitalVendor.toLowerCase().trim();
        const socialVendors = ['facebook', 'instagram', 'youtube', 'tiktok', 'twitter', 'linkedin', 'snapchat'];
        return socialVendors.some(vendor => vendorLower.includes(vendor));
    }

    /**
     * Transform campaigns-data API response to component format
     * Handles the actual API structure: {count, results: [campaigns...]}
     */
    transformFinancialData(apiData) {
        console.log('🔄 transformFinancialData called with:', {
            hasResults: !!apiData.results,
            hasCampaigns: !!apiData.campaigns,
            isArray: Array.isArray(apiData),
            keys: Object.keys(apiData || {})
        });
        
        // Handle campaigns-data API response structure
        const campaigns = apiData.results || apiData.campaigns || apiData;
        
        if (!campaigns || !Array.isArray(campaigns)) {
            console.warn('No campaigns data found in API response');
            return [];
        }

        const transformedData = [];

        campaigns.forEach(campaign => {
            if (!campaign.media_types || !Array.isArray(campaign.media_types)) {
                return;
            }

            campaign.media_types.forEach(mediaType => {
                // Enhanced ICON classification - check both media type and digital vendor
                const isIconByMediaType = this.isIconMediaType(mediaType.media_type);
                
                // Process monthly data if available
                if (mediaType.monthly_data && Array.isArray(mediaType.monthly_data)) {
                    mediaType.monthly_data.forEach(monthlyData => {
                        // Check if this specific monthly entry is social based on digital vendor
                        const isIconByVendor = this.isIconDigitalVendor(monthlyData.digital_vendor);
                        const isIconType = isIconByMediaType || isIconByVendor;
                        const baseData = {
                            // Campaign info
                            campaign_name: campaign.campaign_name,
                            brand_name: campaign.brand_name,
                            sub_brand_name: campaign.sub_brand_name,
                            
                            // Media type info
                            media_type: mediaType.media_type,
                            sub_media_type: monthlyData.sub_media_type || mediaType.sub_media_type,
                            digital_vendor: monthlyData.digital_vendor,
                            region_focus: monthlyData.region_focus,
                            ooh_format: monthlyData.ooh_format,
                            
                            // Financial data
                            planning_gross_cost: parseFloat(monthlyData.planning_gross_cost || 0),
                            actual_gross_cost: parseFloat(monthlyData.actual_gross_cost || 0),
                            provisional_saving: parseFloat(monthlyData.provisional_saving || 0),
                            final_saving: parseFloat(monthlyData.final_saving || 0),
                            net_net_cost: parseFloat(monthlyData.net_net_cost || 0),
                            
                            // Additional fields
                            tracking_fees: parseFloat(monthlyData.tracking_fees || 0),
                            whtax: parseFloat(monthlyData.whtax || 0),
                            ooscope_fees: parseFloat(monthlyData.ooscope_fees || 0),
                            cost_on_coupon_voucher_etc: parseFloat(monthlyData.cost_on_coupon_voucher_etc || 0),
                            
                            // Month info
                            month: monthlyData.month,
                            monthly_status: monthlyData.monthly_status,
                            
                            // Classification flags
                            is_social: isIconType,
                            section: isIconType ? 'ICON' : 'RS_COST'
                        };
                        
                        // Set given amounts based on media type classification
                        // Use monthlyData.given for individual monthly entries
                        const givenAmount = parseFloat(monthlyData.given || monthlyData.planning_gross_cost || 0);
                        
                        if (isIconType) {
                            // ICON (social) - amount goes to given_social
                            baseData.given_paid_media = 0;
                            baseData.given_social = givenAmount;
                            baseData.given = givenAmount;
                        } else {
                            // RS (Red Star/paid) - amount goes to given_paid_media
                            baseData.given_paid_media = givenAmount;
                            baseData.given_social = 0;
                            baseData.given = givenAmount;
                        }
                        
                        // Debug significant amounts
                        if (givenAmount > 100000000) {
                            console.log('💰 Large amount processed:', {
                                campaign: campaign.campaign_name,
                                mediaType: mediaType.media_type,
                                digitalVendor: monthlyData.digital_vendor,
                                isIconType,
                                givenAmount,
                                rawGiven: monthlyData.given,
                                rawPlanning: monthlyData.planning_gross_cost,
                                finalData: {
                                    given: baseData.given,
                                    given_paid_media: baseData.given_paid_media,
                                    given_social: baseData.given_social,
                                    is_social: baseData.is_social
                                }
                            });
                        }
                        
                        transformedData.push(baseData);
                    });
                } else {
                    // No monthly data, create single entry using media type level data
                    const isIconType = isIconByMediaType; // Use media type classification when no monthly data
                    
                    const baseData = {
                        campaign_name: campaign.campaign_name,
                        brand_name: campaign.brand_name,
                        sub_brand_name: campaign.sub_brand_name,
                        
                        media_type: mediaType.media_type,
                        sub_media_type: mediaType.sub_media_type,
                        
                        planning_gross_cost: parseFloat(mediaType.planning_gross_cost || 0),
                        actual_gross_cost: parseFloat(mediaType.actual_gross_cost || 0),
                        provisional_saving: parseFloat(mediaType.provisional_saving || 0),
                        final_saving: parseFloat(mediaType.final_saving || 0),
                        
                        is_social: isIconType,
                        section: isIconType ? 'ICON' : 'RS_COST'
                    };
                    
                    // Use media type level given amount
                    const givenAmount = parseFloat(mediaType.given || mediaType.planning_gross_cost || 0);
                    
                    if (isIconType) {
                        // ICON (social) - amount goes to given_social
                        baseData.given_paid_media = 0;
                        baseData.given_social = givenAmount;
                        baseData.given = givenAmount;
                    } else {
                        // RS (Red Star/paid) - amount goes to given_paid_media
                        baseData.given_paid_media = givenAmount;
                        baseData.given_social = 0;
                        baseData.given = givenAmount;
                    }
                    
                    transformedData.push(baseData);
                }
            });
        });

        // Log the transformation results for debugging
        const iconCount = transformedData.filter(item => item.is_social).length;
        const rsCount = transformedData.filter(item => !item.is_social).length;
        const iconItems = transformedData.filter(item => item.is_social);
        const rsItems = transformedData.filter(item => !item.is_social);
        
        console.log(`✅ Financial data transformation completed:`, {
            campaigns: campaigns.length,
            totalEntries: transformedData.length,
            iconEntries: iconCount,
            rsEntries: rsCount,
            iconTotal: iconItems.reduce((sum, item) => sum + item.given, 0),
            rsTotal: rsItems.reduce((sum, item) => sum + item.given, 0),
            sampleIconItems: iconItems.slice(0, 2).map(item => ({
                mediaType: item.media_type,
                digitalVendor: item.digital_vendor,
                given: item.given,
                planning: item.planning_gross_cost
            })),
            sampleRsItems: rsItems.slice(0, 2).map(item => ({
                mediaType: item.media_type,
                digitalVendor: item.digital_vendor, 
                given: item.given,
                planning: item.planning_gross_cost
            })),
            mediaTypeBreakdown: transformedData.reduce((acc, item) => {
                const key = `${item.media_type} (${item.is_social ? 'ICON' : 'RS'})`;
                acc[key] = (acc[key] || 0) + 1;
                return acc;
            }, {})
        });

        return transformedData;
    }

    /**
     * Fetch data when in snapshot mode (historical data from calendar filter)
     * Uses new snapshot dashboard APIs for 3-table structure
     */
    async fetchSnapshotModeData(options) {
        const { snapshotDate, brandId, mediaType, useCache, cacheKey } = options;
        
        try {
            console.log(`📅 Fetching snapshot mode data for: ${snapshotDate}`);
            
            // Use new snapshot dashboard API for enhanced metrics
            const params = new URLSearchParams();
            params.append('snapshot_date', snapshotDate);
            if (brandId) params.append('brand_id', brandId);
            if (mediaType) params.append('media_type', mediaType);
            
            const endpoint = `snapshot-dashboard/enhanced-metrics/?${params.toString()}`;
            
            console.log(`🔄 Using new snapshot dashboard API: ${endpoint}`);
            
            const response = await this.apiCallWithRetry(
                () => api.get(endpoint, {
                    timeout: 10000,
                    headers: {
                        ...this.defaultConfig.headers,
                        ...getAuthHeaders()
                    }
                }),
                2,
                1000
            );
            
            if (response.data && response.data.financial_metrics) {
                // Transform new snapshot dashboard data to component format
                const transformedData = this.transformSnapshotDashboardData(response.data);
                
                console.log(`✅ Snapshot mode data loaded successfully:`, {
                    endpoint,
                    snapshotDate,
                    entries: transformedData.length,
                    financialMetrics: response.data.financial_metrics
                });
                
                const formattedResponse = {
                    data: transformedData,
                    meta: {
                        timestamp: new Date().toISOString(),
                        snapshotDate: snapshotDate,
                        isSnapshotMode: true,
                        hasSnapshot: false, // No n-1 comparison in snapshot mode
                        dataSource: 'new_snapshot_dashboard_api',
                        originalApiData: response.data
                    }
                };

                // Cache the successful response
                if (useCache) {
                    this.setCache(cacheKey, formattedResponse);
                }

                return formattedResponse;
            } else {
                console.warn(`⚠️ No data returned from snapshot dashboard API for ${snapshotDate}`);
                return { data: [], meta: { snapshotDate, isSnapshotMode: true, hasSnapshot: false } };
            }
            
        } catch (error) {
            console.error(`❌ Failed to fetch snapshot mode data for ${snapshotDate}:`, error.message);
            // Fallback to old snapshot API
            return await this.fetchLegacySnapshotData(snapshotDate);
        }
    }

    /**
     * Transform snapshot data from new-snapshots/campaigns/ API
     */
    transformSnapshotData(apiData) {
        console.log('🔄 Transforming snapshot data:', {
            responseKeys: Object.keys(apiData || {}),
            isArray: Array.isArray(apiData),
            hasResults: !!apiData?.results,
            hasCampaigns: !!apiData?.campaigns
        });

        let campaigns = [];

        // Handle different response formats from snapshot API
        if (Array.isArray(apiData)) {
            campaigns = apiData;
        } else if (apiData?.results && Array.isArray(apiData.results)) {
            campaigns = apiData.results;
        } else if (apiData?.campaigns && Array.isArray(apiData.campaigns)) {
            campaigns = apiData.campaigns;
        } else if (apiData?.data && Array.isArray(apiData.data)) {
            campaigns = apiData.data;
        } else {
            console.warn('⚠️ Unexpected snapshot API response format:', apiData);
            return [];
        }

        console.log(`📊 Processing ${campaigns.length} snapshot campaigns`);

        // Debug: Show all unique media types in snapshot data
        const allMediaTypes = [...new Set(campaigns.map(c => c.media_type).filter(Boolean))];
        const allSubMediaTypes = [...new Set(campaigns.map(c => c.sub_media_type).filter(Boolean))];
        const allDigitalVendors = [...new Set(campaigns.map(c => c.digital_vendor).filter(Boolean))];
        
        console.log('📊 Snapshot Data Media Types Analysis:', {
            uniqueMediaTypes: allMediaTypes,
            uniqueSubMediaTypes: allSubMediaTypes,
            uniqueDigitalVendors: allDigitalVendors,
            hasSocialMediaType: allMediaTypes.some(type => type?.toLowerCase().includes('social')),
            hasSocialSubType: allSubMediaTypes.some(type => type?.toLowerCase().includes('social')),
            hasSocialVendor: allDigitalVendors.some(vendor => vendor?.toLowerCase().includes('social')),
            socialKeywordsFound: {
                mediaTypes: allMediaTypes.filter(type => 
                    ['social', 'facebook', 'instagram', 'youtube', 'tiktok', 'twitter', 'linkedin'].some(keyword => 
                        type?.toLowerCase().includes(keyword)
                    )
                ),
                subMediaTypes: allSubMediaTypes.filter(type => 
                    ['social', 'facebook', 'instagram', 'youtube', 'tiktok', 'twitter', 'linkedin'].some(keyword => 
                        type?.toLowerCase().includes(keyword)
                    )
                ),
                digitalVendors: allDigitalVendors.filter(vendor => 
                    ['social', 'facebook', 'instagram', 'youtube', 'tiktok', 'twitter', 'linkedin', 'meta'].some(keyword => 
                        vendor?.toLowerCase().includes(keyword)
                    )
                )
            }
        });

        const transformedData = [];
        
        campaigns.forEach(campaign => {
            // Get campaign-level information
            const campaignName = campaign.campaign_name || campaign.name || 'Unknown Campaign';
            const brandName = campaign.brand?.brand_name || campaign.brand_name || campaign.brand || 'Unknown Brand';
            const subBrandName = campaign.sub_brand?.sub_brand_name || campaign.sub_brand_name || campaign.sub_brand || '';
            
            // Debug brand extraction
            console.log('🏢 Brand Debug for campaign:', {
                campaignName,
                brandObject: campaign.brand,
                hasBrand: !!campaign.brand,
                brandName: brandName,
                rawBrandData: {
                    brand: campaign.brand,
                    brand_name: campaign.brand_name,
                    fallback_brand: campaign.brand
                }
            });
            
            // Process each media type within the campaign
            const mediaTypes = campaign.media_types || [];
            
            if (mediaTypes.length === 0) {
                console.warn(`⚠️ Campaign ${campaignName} has no media types`);
                return;
            }
            
            mediaTypes.forEach(mediaType => {
                // Get media type level information
                const mediaTypeName = mediaType.media_type || 'Mixed Media';
                const subMediaType = mediaType.sub_media_type;
                const digitalVendor = mediaType.digital_vendor;
                
                // Check if this media type is social media (ICON) or not (RS)
                const isSocial = this.determineIfSocial(mediaTypeName, subMediaType, digitalVendor);
                
                // Process monthly data for this media type
                const monthlyDataList = mediaType.monthly_data || [];
                
                if (monthlyDataList.length === 0) {
                    // If no monthly data, create one entry with media type level data
                    const baseData = {
                        campaign_name: campaignName,
                        brand_name: brandName,
                        sub_brand_name: subBrandName,
                        
                        media_type: mediaTypeName,
                        sub_media_type: subMediaType,
                        digital_vendor: digitalVendor,
                        
                        is_social: isSocial,
                        section: isSocial ? 'ICON' : 'RS_COST',
                        
                        // Use media type level given amount
                        planning_gross_cost: parseFloat(mediaType.media_type_given || 0),
                        actual_gross_cost: 0,
                        month: null
                    };
                    
                    const totalGivenAmount = parseFloat(mediaType.media_type_given || 0);
                    
                    if (isSocial) {
                        baseData.given_paid_media = 0;
                        baseData.given_social = totalGivenAmount;
                        baseData.given = totalGivenAmount;
                    } else {
                        baseData.given_paid_media = totalGivenAmount;
                        baseData.given_social = 0;
                        baseData.given = totalGivenAmount;
                    }
                    
                    console.log('📸 Snapshot Media Type Debug:', {
                        campaign: campaignName,
                        brand: brandName,
                        mediaType: mediaTypeName,
                        subMediaType,
                        digitalVendor,
                        isSocial,
                        amount: totalGivenAmount,
                        classification: isSocial ? 'ICON (Social)' : 'RS (Paid Media)'
                    });
                    
                    transformedData.push(baseData);
                } else {
                    // Create an entry for each monthly data point
                    monthlyDataList.forEach(monthlyData => {
                        const baseData = {
                            campaign_name: campaignName,
                            brand_name: brandName,
                            sub_brand_name: subBrandName,
                            
                            media_type: mediaTypeName,
                            sub_media_type: subMediaType,
                            digital_vendor: digitalVendor,
                            
                            is_social: isSocial,
                            section: isSocial ? 'ICON' : 'RS_COST',
                            
                            month: monthlyData.month,
                            planning_gross_cost: parseFloat(monthlyData.planning_gross_cost || 0),
                            actual_gross_cost: parseFloat(monthlyData.actual_gross_cost || 0),
                            provisional_saving: parseFloat(monthlyData.provisional_saving || 0),
                            final_saving: parseFloat(monthlyData.final_saving || 0)
                        };
                        
                        const totalGivenAmount = parseFloat(monthlyData.given || 0);
                        
                        if (isSocial) {
                            baseData.given_paid_media = 0;
                            baseData.given_social = totalGivenAmount;
                            baseData.given = totalGivenAmount;
                        } else {
                            baseData.given_paid_media = totalGivenAmount;
                            baseData.given_social = 0;
                            baseData.given = totalGivenAmount;
                        }
                        
                        console.log('📸 Snapshot Monthly Data Debug:', {
                            campaign: campaignName,
                            brand: brandName,
                            mediaType: mediaTypeName,
                            subMediaType,
                            digitalVendor,
                            month: monthlyData.month,
                            isSocial,
                            amount: totalGivenAmount,
                            classification: isSocial ? 'ICON (Social)' : 'RS (Paid Media)'
                        });
                        
                        transformedData.push(baseData);
                    });
                }
            });
        });

        // Summary of classification and amount split results
        const iconItems = transformedData.filter(item => item.is_social);
        const rsItems = transformedData.filter(item => !item.is_social);
        const iconCount = iconItems.length;
        const rsCount = rsItems.length;
        
        // Calculate totals for each column
        const totalGivenPaidMedia = transformedData.reduce((sum, item) => sum + item.given_paid_media, 0);
        const totalGivenSocial = transformedData.reduce((sum, item) => sum + item.given_social, 0);
        const iconTotalSocial = iconItems.reduce((sum, item) => sum + item.given_social, 0);
        const rsTotalPaidMedia = rsItems.reduce((sum, item) => sum + item.given_paid_media, 0);
        
        console.log(`✅ Snapshot data classification & amount split summary:`, {
            totalItems: transformedData.length,
            classification: {
                iconItems: iconCount,
                rsItems: rsCount
            },
            amountSplit: {
                totalGivenPaidMedia: totalGivenPaidMedia,
                totalGivenSocial: totalGivenSocial,
                iconTotalSocial: iconTotalSocial,
                rsTotalPaidMedia: rsTotalPaidMedia,
                splitIsCorrect: (totalGivenPaidMedia + totalGivenSocial) > 0
            },
            brandCheck: {
                uniqueBrands: [...new Set(transformedData.map(item => item.brand_name))],
                unknownBrandCount: transformedData.filter(item => item.brand_name === 'Unknown Brand').length,
                hasProperBrands: transformedData.filter(item => item.brand_name !== 'Unknown Brand').length
            },
            iconSamples: iconItems.slice(0, 3).map(item => ({
                campaign: item.campaign_name,
                brand: item.brand_name,
                mediaType: item.media_type,
                digitalVendor: item.digital_vendor,
                section: item.section,
                given_social: item.given_social,
                given_paid_media: item.given_paid_media
            })),
            rsSamples: rsItems.slice(0, 3).map(item => ({
                campaign: item.campaign_name,
                brand: item.brand_name,
                mediaType: item.media_type,
                digitalVendor: item.digital_vendor,
                section: item.section,
                given_social: item.given_social,
                given_paid_media: item.given_paid_media
            }))
        });
        
        return transformedData;
    }

    /**
     * Helper method to determine if media type is social - unified with isIconMediaType logic
     */
    determineIfSocial(mediaType, subMediaType, digitalVendor) {
        if (!mediaType) return false;
        
        const type = (mediaType || '').toLowerCase().trim();
        const subType = (subMediaType || '').toLowerCase().trim();
        const vendor = (digitalVendor || '').toLowerCase().trim();
        
        // Direct social media type
        if (type === 'social') return true;
        
        // Use same keywords as isIconMediaType for consistency
        const socialKeywords = ['social', 'facebook', 'instagram', 'youtube', 'tiktok', 'twitter', 'linkedin'];
        
        // Check media type
        if (socialKeywords.some(keyword => type.includes(keyword))) return true;
        
        // Check sub media type
        if (socialKeywords.some(keyword => subType.includes(keyword))) return true;
        
        // Check digital vendor
        if (socialKeywords.some(keyword => vendor.includes(keyword))) return true;
        
        // Additional checks for vendor variations
        if (vendor.includes('meta') || vendor.includes('snapchat')) return true;
        if (subType.includes('sns') || subType.includes('social media')) return true;
        
        return false;
    }

    /**
     * Transform new snapshot dashboard API data to component format
     */
    transformSnapshotDashboardData(apiData) {
        // For now, create a basic transformation
        // This would need to be expanded based on actual new API structure
        const transformedData = [];
        
        if (apiData.brand_performance && Array.isArray(apiData.brand_performance)) {
            apiData.brand_performance.forEach(brand => {
                transformedData.push({
                    campaign_name: `${brand.brand_name} Campaign`,
                    brand_name: brand.brand_name,
                    sub_brand_name: '',
                    
                    media_type: 'Mixed Media',
                    sub_media_type: null,
                    
                    given: brand.brand_budget || 0,
                    given_paid_media: brand.brand_budget || 0,
                    given_social: 0,
                    planning_gross_cost: brand.brand_planning || 0,
                    actual_gross_cost: brand.brand_actual || 0,
                    
                    is_social: false,
                    section: 'RS_COST'
                });
            });
        }
        
        return transformedData;
    }

    /**
     * Fallback method for legacy snapshot data
     */
    async fetchLegacySnapshotData(snapshotDate) {
        // Convert YYYY-MM-DD to YYYY-MM format if needed
        const snapshotMonth = snapshotDate.substring(0, 7);
        return await this.fetchSnapshotData(snapshotMonth);
    }

    /**
     * Fetch snapshot data for comparison (n-1 month)
     * Uses the correct snapshot endpoint: campaigns-data/?month=
     */
    async fetchSnapshotData(snapshotMonth) {
        try {
            console.log(`📅 Fetching snapshot data for: ${snapshotMonth}`);
            
            // Use the correct working snapshot endpoint
            const endpoint = `campaigns-data/?month=${snapshotMonth}`;
            
            console.log(`🔄 Using snapshot endpoint: ${endpoint}`);
            
            const response = await this.apiCallWithRetry(
                () => api.get(endpoint, {
                    timeout: 10000,
                    headers: {
                        ...this.defaultConfig.headers,
                        ...getAuthHeaders()
                    }
                }),
                2, // Allow 2 retries for the working endpoint
                1000
            );
            
            if (response.data && (response.data.results || response.data.count > 0)) {
                const transformedData = this.transformFinancialData(response.data);
                
                console.log(`✅ Snapshot data loaded successfully:`, {
                    endpoint,
                    snapshotMonth,
                    entries: transformedData.length,
                    iconEntries: transformedData.filter(item => item.is_social).length,
                    rsEntries: transformedData.filter(item => !item.is_social).length
                });
                
                return transformedData;
            } else {
                console.warn(`⚠️ No data returned from snapshot endpoint for ${snapshotMonth}`);
                return [];
            }
            
        } catch (error) {
            console.error(`❌ Failed to fetch snapshot data for ${snapshotMonth}:`, error.message);
            return [];
        }
    }

    /**
     * Fetch financial data for the table (current + snapshot)
     */
    async fetchFinancialData(options = {}) {
        const {
            year,
            month,
            brandId,
            campaignId,
            mediaType,
            useCache = true,
            forceRefresh = false,
            timeout = this.defaultConfig.timeout,
            isSnapshotMode = false,
            snapshotDate = null
        } = options;

        const cacheKey = this.getCacheKey({...options, isSnapshotMode, snapshotDate});

        // Check cache first (unless force refresh)
        if (useCache && !forceRefresh) {
            const cachedData = this.getCache(cacheKey);
            if (cachedData) {
                console.log(`Financial data served from cache: ${cacheKey}`);
                return cachedData;
            }
        }

        // Build API parameters for current data (live data - no month filter)
        const apiParams = {};
        if (year) apiParams.year = year;
        // Note: Don't include month for current mode - we want live data
        // if (month) apiParams.month = month; // Commented out for current mode
        if (brandId) apiParams.brand_id = brandId;
        if (campaignId) apiParams.campaign_id = campaignId;
        if (mediaType) apiParams.media_type = mediaType;

        if (isSnapshotMode && snapshotDate) {
            console.log(`🎯 SNAPSHOT MODE DETECTED: isSnapshotMode=${isSnapshotMode}, snapshotDate=${snapshotDate}`);
            
            // For snapshot mode, use new-snapshots API with snapshot_date parameter
            // Backend expects YYYY-MM format
            
            // Build query parameters for new-snapshots API
            const params = new URLSearchParams();
            params.append('snapshot_date', snapshotDate);
            if (brandId) params.append('brand_id', brandId);
            if (campaignId) params.append('campaign_id', campaignId);
            if (mediaType) params.append('media_type', mediaType);
            
            const endpoint = `new-snapshots/campaigns/?${params.toString()}`;
            console.log(`📸 SNAPSHOT MODE: Fetching snapshot data for ${snapshotDate} from: ${endpoint}`);
            console.log(`🔍 Full API URL: ${api.defaults.baseURL || 'http://localhost:8000/api/'}${endpoint}`);

            // Check authentication before making request
            if (!isAuthenticated()) {
                throw new Error('User not authenticated. Please log in.');
            }

            try {
                const response = await this.apiCallWithRetry(
                    () => api.get(endpoint, { 
                        timeout,
                        headers: {
                            ...this.defaultConfig.headers,
                            ...getAuthHeaders()
                        }
                    }),
                    2,
                    1000
                );

                if (response && response.data) {
                    console.log(`✅ Snapshot data fetched successfully from: ${endpoint}`);
                    console.log(`📋 New-snapshots API response structure:`, {
                        responseKeys: Object.keys(response.data),
                        isArray: Array.isArray(response.data),
                        hasResults: !!response.data.results,
                        hasCampaigns: !!response.data.campaigns,
                        hasCount: !!response.data.count,
                        sampleStructure: response.data.campaigns ? response.data.campaigns[0] : 
                                       response.data.results ? response.data.results[0] :
                                       Array.isArray(response.data) ? response.data[0] : 'No array found'
                    });
                    
                    // Handle new-snapshots API response - determine which transformer to use
                    let allTransformedData;
                    
                    // Check structure to decide transformer
                    if (response.data.campaigns && Array.isArray(response.data.campaigns)) {
                        // Structure: {campaigns: [...]} - use transformFinancialData (campaigns-data format)
                        console.log(`🔄 Using transformFinancialData for new-snapshots API (campaigns format)`);
                        allTransformedData = this.transformFinancialData(response.data);
                    } else if (Array.isArray(response.data)) {
                        // Structure: [...] - use transformSnapshotData (direct array format)
                        console.log(`🔄 Using transformSnapshotData for new-snapshots API (array format)`);
                        allTransformedData = this.transformSnapshotData(response.data);
                    } else {
                        // Try snapshot transformer first, then fallback
                        try {
                            console.log(`🔄 Trying transformSnapshotData first for new-snapshots API`);
                            allTransformedData = this.transformSnapshotData(response.data);
                        } catch (error) {
                            console.log(`⚠️ Snapshot transformer failed, trying standard transformer:`, error.message);
                            allTransformedData = this.transformFinancialData(response.data);
                        }
                    }
                    
                    const formattedResponse = {
                        data: allTransformedData,
                        meta: {
                            timestamp: new Date().toISOString(),
                            snapshotDate: snapshotDate,
                            isSnapshotMode: true,
                            hasSnapshot: false, // No auto n-1 fetch in snapshot mode
                            dataSource: 'new_snapshots_campaigns_api'
                        }
                    };

                    // Cache the successful response
                    if (useCache) {
                        this.setCache(cacheKey, formattedResponse);
                    }

                    return formattedResponse;
                } else {
                    throw new Error('No data in snapshot response');
                }

            } catch (error) {
                console.error(`❌ new-snapshots API failed (${error.status || error.response?.status || 'unknown'}):`, error.message);
                console.error(`❌ Full error details:`, error);
                
                // TEMPORARILY DISABLE FALLBACK - Force fix new-snapshots API
                console.log(`🚫 FALLBACK DISABLED: Must fix new-snapshots API instead of fallback`);
                
                // Return error instead of fallback
                throw new Error(`new-snapshots API failed: ${error.message}. Status: ${error.response?.status || 'unknown'}`);
                
                /*
                // Fallback to campaigns-data with month parameter for snapshot data
                console.log(`🔄 FALLBACK: Falling back to campaigns-data API for snapshot data: ${snapshotDate}`);
                
                try {
                    const fallbackEndpoint = `campaigns-data/?month=${snapshotDate}`;
                    console.log(`📅 Trying fallback snapshot endpoint: ${fallbackEndpoint}`);
                    
                    const fallbackResponse = await this.apiCallWithRetry(
                        () => api.get(fallbackEndpoint, { 
                            timeout,
                            headers: {
                                ...this.defaultConfig.headers,
                                ...getAuthHeaders()
                            }
                        }),
                        2,
                        1000
                    );

                    if (fallbackResponse && fallbackResponse.data) {
                        console.log(`✅ Snapshot data fetched from fallback: ${fallbackEndpoint}`);
                        
                        // Handle campaigns-data API response (same as current data)
                        const allTransformedData = this.transformFinancialData(fallbackResponse.data);
                        
                        const formattedResponse = {
                            data: allTransformedData,
                            meta: {
                                timestamp: new Date().toISOString(),
                                snapshotDate: snapshotDate,
                                isSnapshotMode: true,
                                hasSnapshot: false,
                                dataSource: 'campaigns_data_fallback'
                            }
                        };

                        // Cache the successful response
                        if (useCache) {
                            this.setCache(cacheKey, formattedResponse);
                        }

                        return formattedResponse;
                    } else {
                        throw new Error('No data in fallback response');
                    }
                    
                } catch (fallbackError) {
                    console.error(`❌ Fallback also failed:`, fallbackError.message);
                    throw new Error(`Both snapshot APIs failed: ${error.message} | ${fallbackError.message}`);
                }
                */
            }
        }

        try {
            // Current data mode - fetch live data (no month filter)
            console.log(`📊 CURRENT MODE: Fetching live data from campaigns-data API`);

            // Check authentication before making request
            if (!isAuthenticated()) {
                throw new Error('User not authenticated. Please log in.');
            }

            // Try multiple endpoints until one works
            let response = null;
            let lastError = null;
            let successfulEndpoint = null;
            
            // List of endpoints to try in order (no leading slashes)
            const endpointsToTry = [
                this.buildEndpoint(apiParams, 'campaigns-data/'),
                this.buildEndpoint(apiParams, 'total-data/'),
                this.buildEndpoint({}, 'all-campaigns/'),
                this.buildEndpoint(apiParams, 'reports/brand-summaries/'),
                this.buildEndpoint(apiParams, 'dashboard/budget-data/')
            ];
            
            for (const tryEndpoint of endpointsToTry) {
                try {
                    console.log(`Trying endpoint: ${tryEndpoint}`);
                    
                    response = await this.apiCallWithRetry(
                        () => api.get(tryEndpoint, { 
                            timeout,
                            headers: {
                                ...this.defaultConfig.headers,
                                ...getAuthHeaders()
                            }
                        }),
                        2, // Fewer retries per endpoint
                        500 // Shorter delay
                    );
                    
                    console.log(`✅ Success with endpoint: ${tryEndpoint}`);
                    successfulEndpoint = tryEndpoint;
                    break; // Success, break out of loop
                    
                } catch (error) {
                    console.log(`❌ Failed endpoint: ${tryEndpoint}`, error.message);
                    lastError = error;
                    continue; // Try next endpoint
                }
            }
            
            if (!response) {
                throw lastError || new Error('All endpoints failed');
            }

            // Debug API response structure
            console.log(`🔍 API Response Debug:`, {
                endpoint: successfulEndpoint,
                isSnapshotMode: isSnapshotMode,
                responseKeys: Object.keys(response.data || {}),
                responseSize: JSON.stringify(response.data || {}).length,
                firstItems: Array.isArray(response.data?.results) ? 
                    response.data.results.slice(0, 2) : 
                    Array.isArray(response.data?.campaigns) ? 
                        response.data.campaigns.slice(0, 2) :
                        Array.isArray(response.data) ? 
                            response.data.slice(0, 2) : 'No array data'
            });

            // Transform data to component format with conditional logic
            let allTransformedData;
            
            if (isSnapshotMode && snapshotDate) {
                // Handle snapshot API response format
                console.log(`🔄 Transforming snapshot data for ${snapshotDate}`);
                allTransformedData = this.transformSnapshotData(response.data);
            } else {
                // Handle campaigns-data API response format
                console.log(`🔄 Transforming current data`);
                allTransformedData = this.transformFinancialData(response.data);
            }
            
            // Get month info for proper current month calculation
            const monthInfo = this.getCurrentMonthParams();
            const currentMonthString = `${monthInfo.thisYear}-${String(monthInfo.thisMonth).padStart(2, '0')}`;
            
            // Debug: Log all months in the data to understand what's available
            const uniqueMonths = [...new Set(allTransformedData.map(item => item.month).filter(Boolean))];
            console.log(`🔍 Available months in API data:`, uniqueMonths.sort());
            
            // For debugging, let's temporarily show all data without filtering
            // TODO: Re-enable filtering once we understand the data structure
            const transformedData = allTransformedData;
            
            // Original filtering logic (temporarily disabled for debugging)
            /*
            const transformedData = allTransformedData.filter(item => {
                if (!item.month) {
                    // If no month specified, include in current data as fallback
                    console.log('⚠️ Item without month field included in current data:', item.campaign_name);
                    return true;
                }
                
                // Check if item belongs to current month based on business logic
                return item.month.includes(currentMonthString);
            });
            */
            
            console.log(`🔄 Filtered current data for month (${currentMonthString}):`, {
                totalEntries: allTransformedData.length,
                currentMonthEntries: transformedData.length,
                filteredOut: allTransformedData.length - transformedData.length,
                businessLogic: {
                    thisMonth: monthInfo.thisMonth,
                    thisYear: monthInfo.thisYear,
                    isSnapshotReady: monthInfo.isSnapshotReady
                },
                sampleCurrentItems: transformedData.slice(0, 3).map(item => ({
                    campaign: item.campaign_name,
                    month: item.month,
                    given: item.given
                }))
            });
            
            // Skip auto-fetching snapshot data if we're already in snapshot mode
            // (Component will handle dual fetching)
            let snapshotData = [];
            let snapshotMonth = null;
            
            if (!isSnapshotMode) {
                // Only fetch snapshot data for current mode (not in snapshot mode)
                snapshotMonth = `${monthInfo.lastYear}-${String(monthInfo.lastMonth).padStart(2, '0')}`;
                
                try {
                    snapshotData = await this.fetchSnapshotData(snapshotMonth);
                    console.log(`📅 Snapshot data fetched for ${snapshotMonth}: ${snapshotData.length} entries`);
                } catch (error) {
                    console.warn(`⚠️ Could not fetch snapshot data for ${snapshotMonth}:`, error.message);
                }
            } else {
                console.log(`🔄 Skipping auto-snapshot fetch in snapshot mode (handled by component dual fetching)`);
            }
            
            // Debug the data classification issue
            console.log('✅ Data transformation complete:', {
                currentDataEntries: transformedData.length,
                snapshotDataEntries: snapshotData.length,
                hasRealData: transformedData.length > 100,
                iconEntries: transformedData.filter(item => item.is_social).length,
                rsEntries: transformedData.filter(item => !item.is_social).length
            });
            
            const formattedResponse = {
                data: transformedData,
                meta: {
                    timestamp: new Date().toISOString(),
                    totalCampaigns: response.data?.count || response.data?.results?.length || 0,
                    snapshotMonth: snapshotMonth,
                    hasSnapshot: snapshotData.length > 0,
                    snapshotData: snapshotData,
                    ...monthInfo
                }
            };

            // Cache the successful response
            if (useCache) {
                this.setCache(cacheKey, formattedResponse);
            }

            return formattedResponse;

        } catch (error) {
            console.error('All Financial Table API endpoints failed:', error);
            throw error;
        }
    }

    /**
     * Transform data from various API endpoints to unified format
     */
    transformApiData(apiResponse) {
        const data = apiResponse.data || apiResponse;
        
        // Handle different API response formats
        if (data.campaigns) {
            // campaigns-data format
            return this.transformFinancialData(data);
        } else if (Array.isArray(data.results)) {
            // Paginated results format (brand-summaries, etc.)
            return this.transformBrandSummariesData(data);
        } else if (Array.isArray(data)) {
            // Direct array format (all-campaigns, etc.)
            return this.transformDirectArrayData(data);
        } else if (data.brands) {
            // Dashboard format with brands
            return this.transformDashboardData(data);
        }
        
        console.warn('Unknown API response format, returning empty array');
        return [];
    }
    
    /**
     * Transform brand summaries data to financial table format
     */
    transformBrandSummariesData(apiData) {
        if (!apiData.results || !Array.isArray(apiData.results)) {
            return [];
        }
        
        return apiData.results.map(brand => ({
            campaign_name: brand.brand_name || 'Unknown Campaign',
            brand_name: brand.brand_name,
            sub_brand_name: brand.sub_brand_name || '',
            
            media_type: 'Mixed Media',
            sub_media_type: null,
            
            given_paid_media: parseFloat(brand.total_budget || brand.total_given || 0),
            given_social: 0,
            planning_gross_cost: parseFloat(brand.total_planning_gross_cost || brand.total_planning || 0),
            actual_gross_cost: parseFloat(brand.total_actual_gross_cost || brand.total_actual || 0),
            
            is_social: false,
            section: 'RS_COST'
        }));
    }
    
    /**
     * Transform direct array data (all-campaigns)
     */
    transformDirectArrayData(apiData) {
        return apiData.map(item => ({
            campaign_name: item.campaign_name || item.name,
            brand_name: item.brand_name || item.brand,
            sub_brand_name: item.sub_brand_name || '',
            
            media_type: item.media_type || 'Unknown',
            sub_media_type: item.sub_media_type || null,
            
            given_paid_media: parseFloat(item.total_given || item.budget || 0),
            given_social: 0,
            planning_gross_cost: parseFloat(item.total_planning_gross_cost || 0),
            actual_gross_cost: parseFloat(item.total_actual_gross_cost || 0),
            
            is_social: this.isIconMediaType(item.media_type),
            section: this.isIconMediaType(item.media_type) ? 'ICON' : 'RS_COST'
        }));
    }
    
    /**
     * Transform dashboard data format
     */
    transformDashboardData(apiData) {
        const transformedData = [];
        
        if (apiData.brands && Array.isArray(apiData.brands)) {
            apiData.brands.forEach(brand => {
                transformedData.push({
                    campaign_name: `${brand.brand_name} Campaign`,
                    brand_name: brand.brand_name,
                    sub_brand_name: '',
                    
                    media_type: 'Mixed Media',
                    sub_media_type: null,
                    
                    given_paid_media: parseFloat(brand.total_budget || 0),
                    given_social: 0,
                    planning_gross_cost: parseFloat(brand.total_planning || 0),
                    actual_gross_cost: parseFloat(brand.total_actual || 0),
                    
                    is_social: false,
                    section: 'RS_COST'
                });
            });
        }
        
        return transformedData;
    }

    /**
     * Clear all cached data
     */
    clearCache() {
        this.cache.clear();
    }

    /**
     * Get cache statistics
     */
    getCacheStats() {
        return {
            size: this.cache.size,
            maxSize: this.maxCacheSize,
            ttl: this.defaultTTL,
            keys: Array.from(this.cache.keys())
        };
    }
}

// Create singleton instance
const financialTableService = new FinancialTableService();

export default financialTableService;